import { useState, useEffect } from 'react';
import { Link2, Unlink, TrendingUp, FileDown, AlertCircle, CheckCircle, Clock, BarChart3, ChevronDown, ChevronUp, Target, Plus, Info } from 'lucide-react';
import { supabase, Transaction, BudgetLine, BudgetAllocation, Category } from '../lib/supabase';

interface ManualAllocation {
  id: string;
  user_id: string;
  budget_line_id: string;
  allocated_amount: number;
  allocation_date: string;
  reason: string;
  notes?: string;
  created_at: string;
}
import { PDFExporter, formatCurrency, formatCurrencyForPDF } from '../utils/pdfExport';
import { useAuth } from '../contexts/AuthContext';
import { useFiscalYear } from '../hooks/useFiscalYear';
import { getCurrentSchoolYear } from '../utils/schoolYear';
import { ExerciseLandingDrawer } from './ExerciseLandingDrawer';

export function TrackingTab() {
  const { effectiveUserId } = useAuth();
  const { startMonth, startDay, loading: fiscalYearLoading } = useFiscalYear();
  const [transactions, setTransactions] = useState<(Transaction & { category?: Category })[]>([]);
  const [budgetLines, setBudgetLines] = useState<(BudgetLine & { category?: Category })[]>([]);
  const [allocations, setAllocations] = useState<BudgetAllocation[]>([]);
  const [manualAllocations, setManualAllocations] = useState<ManualAllocation[]>([]);
  const [currentYear, setCurrentYear] = useState(0);
  const [expandedBudgetLine, setExpandedBudgetLine] = useState<string | null>(null);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [showManualAllocationModal, setShowManualAllocationModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<string | null>(null);
  const [selectedBudgetLineForModal, setSelectedBudgetLineForModal] = useState<string | null>(null);
  const [allocationAmount, setAllocationAmount] = useState('');
  const [manualAllocationAmount, setManualAllocationAmount] = useState('');
  const [manualAllocationDate, setManualAllocationDate] = useState('');
  const [manualAllocationReason, setManualAllocationReason] = useState('');
  const [manualAllocationNotes, setManualAllocationNotes] = useState('');
  const [activeTab, setActiveTab] = useState<'income' | 'expense' | 'unallocated'>('expense');
  const [isLandingDrawerOpen, setIsLandingDrawerOpen] = useState(false);

  useEffect(() => {
    if (!fiscalYearLoading && startMonth !== null && startDay !== null) {
      const year = getCurrentSchoolYear(startMonth, startDay);
      setCurrentYear(year);
    }
  }, [fiscalYearLoading, startMonth, startDay]);

  useEffect(() => {
    loadData();
  }, [currentYear, effectiveUserId]);

  async function loadData() {
    if (!effectiveUserId || currentYear === 0) return;

    // Calculer les dates de début et fin de l'exercice
    const fiscalYearStart = new Date(currentYear, startMonth - 1, startDay);
    const fiscalYearEnd = new Date(currentYear + 1, startMonth - 1, startDay - 1);

    const startDateStr = fiscalYearStart.toISOString().split('T')[0];
    const endDateStr = fiscalYearEnd.toISOString().split('T')[0];

    const [transactionsRes, budgetLinesRes, allocationsRes, manualAllocationsRes] = await Promise.all([
      supabase
        .from('transactions')
        .select('*, categories(*)')
        .eq('user_id', effectiveUserId)
        .eq('is_internal_transfer', false)
        .gte('date', startDateStr)
        .lte('date', endDateStr)
        .order('date', { ascending: false }),
      supabase
        .from('budget_lines')
        .select('*, categories(*)')
        .eq('user_id', effectiveUserId)
        .eq('year', currentYear)
        .order('planned_amount', { ascending: false }),
      supabase
        .from('budget_allocations')
        .select(`
          *,
          budget_lines!inner(user_id, year),
          transactions!inner(date, is_internal_transfer)
        `)
        .eq('budget_lines.user_id', effectiveUserId)
        .eq('budget_lines.year', currentYear)
        .eq('transactions.is_internal_transfer', false)
        .gte('transactions.date', startDateStr)
        .lte('transactions.date', endDateStr),
      supabase
        .from('budget_manual_allocations')
        .select('*')
        .eq('user_id', effectiveUserId)
    ]);

    if (!transactionsRes.error && transactionsRes.data) {
      setTransactions(transactionsRes.data.map(t => ({ ...t, category: t.categories })));
    }

    if (!budgetLinesRes.error && budgetLinesRes.data) {
      setBudgetLines(budgetLinesRes.data.map(b => ({ ...b, category: b.categories })));
    }

    if (!allocationsRes.error && allocationsRes.data) {
      setAllocations(allocationsRes.data);
    }

    if (!manualAllocationsRes.error && manualAllocationsRes.data) {
      setManualAllocations(manualAllocationsRes.data);
    }
  }

  async function createAllocation() {
    if (!effectiveUserId || !selectedTransaction || !selectedBudgetLineForModal || !allocationAmount) return;

    const { error } = await supabase
      .from('budget_allocations')
      .insert([{
        budget_line_id: selectedBudgetLineForModal,
        transaction_id: selectedTransaction,
        allocated_amount: parseFloat(allocationAmount)
      }]);

    if (!error) {
      loadData();
      setShowAllocationModal(false);
      setSelectedTransaction(null);
      setSelectedBudgetLineForModal(null);
      setAllocationAmount('');
    }
  }

  async function deleteAllocation(id: string) {
    if (!effectiveUserId) return;

    const { error } = await supabase
      .from('budget_allocations')
      .delete()
      .eq('id', id);

    if (!error) {
      loadData();
    }
  }

  async function createManualAllocation() {
    if (!effectiveUserId || !selectedBudgetLineForModal || !manualAllocationAmount || !manualAllocationDate || !manualAllocationReason) return;

    if (manualAllocationReason.length < 10) {
      alert('La justification doit contenir au moins 10 caractères');
      return;
    }

    const { error } = await supabase
      .from('budget_manual_allocations')
      .insert([{
        user_id: effectiveUserId,
        budget_line_id: selectedBudgetLineForModal,
        allocated_amount: parseFloat(manualAllocationAmount),
        allocation_date: manualAllocationDate,
        reason: manualAllocationReason,
        notes: manualAllocationNotes || null
      }]);

    if (!error) {
      loadData();
      setShowManualAllocationModal(false);
      setSelectedBudgetLineForModal(null);
      setManualAllocationAmount('');
      setManualAllocationDate('');
      setManualAllocationReason('');
      setManualAllocationNotes('');
    } else {
      alert('Erreur lors de la création de l\'allocation manuelle: ' + error.message);
    }
  }

  async function deleteManualAllocation(id: string) {
    if (!effectiveUserId) return;

    if (!confirm('Êtes-vous sûr de vouloir supprimer cette allocation manuelle ?')) return;

    const { error } = await supabase
      .from('budget_manual_allocations')
      .delete()
      .eq('id', id);

    if (!error) {
      loadData();
    }
  }

  function openManualAllocationModal(budgetLineId: string) {
    const today = new Date().toISOString().split('T')[0];
    setSelectedBudgetLineForModal(budgetLineId);
    setManualAllocationDate(today);
    setShowManualAllocationModal(true);
  }

  function openAllocationModal(transactionId: string, suggestedBudgetLineId?: string) {
    const transaction = transactions.find(t => t.id === transactionId);
    if (transaction) {
      setSelectedTransaction(transactionId);
      setSelectedBudgetLineForModal(suggestedBudgetLineId || null);
      setAllocationAmount(Math.abs(Number(transaction.amount)).toString());
      setShowAllocationModal(true);
    }
  }

  const budgetSummary = budgetLines.map(line => {
    const autoAllocated = allocations
      .filter(a => a.budget_line_id === line.id)
      .reduce((sum, a) => sum + Number(a.allocated_amount), 0);

    const manualAllocated = manualAllocations
      .filter(a => a.budget_line_id === line.id)
      .reduce((sum, a) => sum + Number(a.allocated_amount), 0);

    const allocated = autoAllocated + manualAllocated;

    const remaining = Number(line.planned_amount) - allocated;
    const consumptionRate = Number(line.planned_amount) !== 0
      ? (allocated / Number(line.planned_amount)) * 100
      : 0;

    const hasManualAllocations = manualAllocations.some(a => a.budget_line_id === line.id);

    return {
      ...line,
      allocated,
      autoAllocated,
      manualAllocated,
      remaining,
      consumptionRate,
      hasManualAllocations
    };
  });

  const toggleBudgetLine = (lineId: string) => {
    setExpandedBudgetLine(expandedBudgetLine === lineId ? null : lineId);
  };

  const allocatedTransactionIds = new Set(allocations.map(a => a.transaction_id));
  const budgetLineCategoryIds = new Set(budgetLines.map(bl => bl.category_id));
  const unallocatedTransactions = transactions.filter(
    t => !allocatedTransactionIds.has(t.id) &&
         t.category_id &&
         budgetLineCategoryIds.has(t.category_id)
  );

  // Calcul des KPIs globaux
  const incomeLines = budgetSummary.filter(line => line.category?.type === 'income');
  const expenseLines = budgetSummary.filter(line => line.category?.type === 'expense');

  const totalPlannedIncome = incomeLines.reduce((sum, line) => sum + Number(line.planned_amount), 0);
  const totalAllocatedIncome = incomeLines.reduce((sum, line) => sum + line.allocated, 0);
  const totalPlannedExpense = expenseLines.reduce((sum, line) => sum + Number(line.planned_amount), 0);
  const totalAllocatedExpense = expenseLines.reduce((sum, line) => sum + line.allocated, 0);

  const globalIncomeRate = totalPlannedIncome !== 0 ? (totalAllocatedIncome / totalPlannedIncome) * 100 : 0;
  const globalExpenseRate = totalPlannedExpense !== 0 ? (totalAllocatedExpense / totalPlannedExpense) * 100 : 0;

  const exportToPDF = () => {
    const pdf = new PDFExporter();

    pdf.getDoc().text('Compte Rendu de Suivi Budgetaire', pdf['pageWidth'] / 2, pdf['currentY'], { align: 'center' });
    pdf['currentY'] += 10;

    pdf.addSection(`Exercice ${currentYear}`);

    const totalPlanned = budgetLines.reduce((sum, line) => sum + Number(line.planned_amount), 0);
    const totalAllocated = budgetSummary.reduce((sum, line) => sum + line.allocated, 0);
    const totalRemaining = budgetSummary.reduce((sum, line) => sum + line.remaining, 0);
    const globalConsumptionRate = totalPlanned !== 0 ? (totalAllocated / totalPlanned) * 100 : 0;

    pdf.addSummaryBox([
      { label: 'Total prevu', value: formatCurrencyForPDF(totalPlanned) },
      { label: 'Total impute', value: formatCurrencyForPDF(totalAllocated) },
      { label: 'Total restant', value: formatCurrencyForPDF(totalRemaining), highlight: true },
      { label: 'Taux de consommation global', value: `${globalConsumptionRate.toFixed(1)}%`, highlight: true }
    ]);

    if (incomeLines.length > 0) {
      pdf.addSection('Recettes - Suivi budgetaire');
      const incomeData = incomeLines.map(line => ({
        categorie: line.category?.name || 'Sans categorie',
        prevu: formatCurrencyForPDF(Number(line.planned_amount)),
        impute: formatCurrencyForPDF(line.allocated),
        restant: formatCurrencyForPDF(line.remaining),
        taux: `${line.consumptionRate.toFixed(1)}%`
      }));

      pdf.addTable(
        [
          { header: 'Categorie', dataKey: 'categorie' },
          { header: 'Prevu', dataKey: 'prevu' },
          { header: 'Impute', dataKey: 'impute' },
          { header: 'Restant', dataKey: 'restant' },
          { header: 'Taux', dataKey: 'taux' }
        ],
        incomeData,
        {
          columnStyles: {
            0: { halign: 'left', cellWidth: 60 },
            1: { halign: 'right', cellWidth: 30 },
            2: { halign: 'right', cellWidth: 30 },
            3: { halign: 'right', cellWidth: 30 },
            4: { halign: 'right', cellWidth: 20 }
          }
        }
      );
    }

    if (expenseLines.length > 0) {
      pdf.addSection('Depenses - Suivi budgetaire');
      const expenseData = expenseLines.map(line => ({
        categorie: line.category?.name || 'Sans categorie',
        prevu: formatCurrencyForPDF(Number(line.planned_amount)),
        impute: formatCurrencyForPDF(line.allocated),
        restant: formatCurrencyForPDF(line.remaining),
        taux: `${line.consumptionRate.toFixed(1)}%`
      }));

      pdf.addTable(
        [
          { header: 'Categorie', dataKey: 'categorie' },
          { header: 'Prevu', dataKey: 'prevu' },
          { header: 'Impute', dataKey: 'impute' },
          { header: 'Restant', dataKey: 'restant' },
          { header: 'Taux', dataKey: 'taux' }
        ],
        expenseData,
        {
          columnStyles: {
            0: { halign: 'left', cellWidth: 60 },
            1: { halign: 'right', cellWidth: 30 },
            2: { halign: 'right', cellWidth: 30 },
            3: { halign: 'right', cellWidth: 30 },
            4: { halign: 'right', cellWidth: 20 }
          }
        }
      );
    }

    if (unallocatedTransactions.length > 0) {
      pdf.addSection('Operations non imputees');
      pdf.addText(`${unallocatedTransactions.length} operation(s) en attente d'imputation au budget previsionnel.`, 9);
    }

    pdf.generate({
      title: 'Compte Rendu de Suivi Budgetaire',
      subtitle: `Exercice ${currentYear}`,
      schoolYear: `${currentYear}`
    });
  };

  if (currentYear === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-gray-500">
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Suivi budgétaire</h2>
          <p className="text-sm text-gray-600 mt-1">Exercice comptable {currentYear}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsLandingDrawerOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-sm hover:shadow-md"
          >
            <Target size={18} />
            Voir l'atterrissage
          </button>
          <button
            onClick={exportToPDF}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FileDown size={18} />
            Exporter PDF
          </button>
        </div>
      </div>

      {budgetLines.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-yellow-800">
            <p className="font-medium mb-1">Aucune ligne budgétaire pour cet exercice</p>
            <p>Rendez-vous dans l'onglet <strong>"Budget prévisionnel"</strong> pour créer votre budget avant de pouvoir imputer des opérations.</p>
          </div>
        </div>
      )}

      {/* KPIs Globaux */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Recettes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="text-green-600" size={20} />
              </div>
              <h3 className="font-semibold text-gray-800">Recettes</h3>
            </div>
            <div className={`text-xs px-2 py-1 rounded-full font-medium ${
              globalIncomeRate > 100 ? 'bg-red-100 text-red-700' :
              globalIncomeRate > 80 ? 'bg-orange-100 text-orange-700' :
              'bg-green-100 text-green-700'
            }`}>
              {globalIncomeRate.toFixed(0)}%
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Prévu</span>
              <span className="font-semibold text-gray-900">{totalPlannedIncome.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Imputé</span>
              <span className="font-semibold text-green-600">{totalAllocatedIncome.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Restant</span>
              <span className="font-semibold text-gray-900">{(totalPlannedIncome - totalAllocatedIncome).toFixed(2)} €</span>
            </div>
          </div>
          <div className="mt-3 bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full transition-all ${
                globalIncomeRate > 100 ? 'bg-red-500' :
                globalIncomeRate > 80 ? 'bg-orange-500' :
                'bg-green-500'
              }`}
              style={{ width: `${Math.min(globalIncomeRate, 100)}%` }}
            />
          </div>
        </div>

        {/* Dépenses */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="text-blue-600" size={20} />
              </div>
              <h3 className="font-semibold text-gray-800">Dépenses</h3>
            </div>
            <div className={`text-xs px-2 py-1 rounded-full font-medium ${
              globalExpenseRate > 100 ? 'bg-red-100 text-red-700' :
              globalExpenseRate > 80 ? 'bg-orange-100 text-orange-700' :
              'bg-green-100 text-green-700'
            }`}>
              {globalExpenseRate.toFixed(0)}%
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Prévu</span>
              <span className="font-semibold text-gray-900">{totalPlannedExpense.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Imputé</span>
              <span className="font-semibold text-blue-600">{totalAllocatedExpense.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Restant</span>
              <span className="font-semibold text-gray-900">{(totalPlannedExpense - totalAllocatedExpense).toFixed(2)} €</span>
            </div>
          </div>
          <div className="mt-3 bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full transition-all ${
                globalExpenseRate > 100 ? 'bg-red-500' :
                globalExpenseRate > 80 ? 'bg-orange-500' :
                'bg-blue-500'
              }`}
              style={{ width: `${Math.min(globalExpenseRate, 100)}%` }}
            />
          </div>
        </div>

        {/* Opérations non imputées */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="text-orange-600" size={20} />
              </div>
              <h3 className="font-semibold text-gray-800">À imputer</h3>
            </div>
            <div className="text-xs px-2 py-1 rounded-full font-medium bg-orange-100 text-orange-700">
              {unallocatedTransactions.length}
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Opérations</span>
              <span className="font-semibold text-gray-900">{unallocatedTransactions.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Montant total</span>
              <span className="font-semibold text-orange-600">
                {unallocatedTransactions.reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0).toFixed(2)} €
              </span>
            </div>
          </div>
          {unallocatedTransactions.length > 0 && (
            <button
              onClick={() => setActiveTab('unallocated')}
              className="mt-4 w-full py-2 text-sm bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors font-medium"
            >
              Voir les opérations
            </button>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('income')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'income'
                  ? 'text-green-600 border-b-2 border-green-600 bg-green-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              Recettes ({incomeLines.length})
            </button>
            <button
              onClick={() => setActiveTab('expense')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'expense'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              Dépenses ({expenseLines.length})
            </button>
            <button
              onClick={() => setActiveTab('unallocated')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'unallocated'
                  ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              Non imputées ({unallocatedTransactions.length})
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Recettes */}
          {activeTab === 'income' && (
            <div className="space-y-3">
              {incomeLines.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle size={48} className="mx-auto mb-4 text-gray-400" />
                  <p>Aucune ligne de recettes pour cet exercice</p>
                </div>
              ) : (
                incomeLines.map(line => {
                  const isExpanded = expandedBudgetLine === line.id;
                  const lineAllocations = allocations.filter(a => a.budget_line_id === line.id);

                  return (
                    <div
                      key={line.id}
                      className={`rounded-lg transition-all border ${
                        isExpanded
                          ? 'border-green-500 bg-green-50 shadow-md'
                          : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                      }`}
                    >
                      <div
                        onClick={() => toggleBudgetLine(line.id)}
                        className="p-4 cursor-pointer"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">{line.category?.name}</h4>
                            {isExpanded ? (
                              <ChevronUp size={18} className="text-gray-500" />
                            ) : (
                              <ChevronDown size={18} className="text-gray-500" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">
                              {lineAllocations.length} op.
                            </span>
                            <div className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                              line.consumptionRate > 100 ? 'bg-red-100 text-red-700' :
                              line.consumptionRate > 80 ? 'bg-orange-100 text-orange-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {line.consumptionRate.toFixed(0)}%
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-4 gap-3 mb-3">
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Prévu</p>
                            <p className="font-semibold text-gray-900 text-sm">{Number(line.planned_amount).toFixed(2)} €</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Imputé</p>
                            <p className="font-semibold text-green-600 text-sm">{line.allocated.toFixed(2)} €</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Restant</p>
                            <p className={`font-semibold text-sm ${line.remaining >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                              {line.remaining.toFixed(2)} €
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Taux</p>
                            <p className="font-semibold text-orange-600 text-sm">{line.consumptionRate.toFixed(1)}%</p>
                          </div>
                        </div>

                        <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              line.consumptionRate > 100 ? 'bg-red-500' :
                              line.consumptionRate > 80 ? 'bg-orange-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(line.consumptionRate, 100)}%` }}
                          />
                        </div>
                      </div>

                      {/* Détail déplié */}
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-green-200 bg-white">
                          {/* Alerte allocations manuelles */}
                          {line.hasManualAllocations && (
                            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                              <Info size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                              <div className="text-xs text-amber-800">
                                <p className="font-medium">Cette ligne contient des allocations manuelles exceptionnelles</p>
                                <p className="mt-1">Montant manuel : {line.manualAllocated.toFixed(2)} €</p>
                              </div>
                            </div>
                          )}

                          <div className="pt-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-semibold text-gray-900 text-sm">
                                Opérations imputées ({lineAllocations.length})
                              </h5>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openManualAllocationModal(line.id);
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors"
                                title="Créer une allocation manuelle (cas exceptionnel)"
                              >
                                <Plus size={14} />
                                Allocation manuelle
                              </button>
                            </div>
                            {lineAllocations.length === 0 ? (
                              <div className="text-center py-6 text-gray-500 text-sm">
                                <p>Aucune opération imputée à cette ligne</p>
                              </div>
                            ) : (
                              <div className="space-y-2 max-h-80 overflow-y-auto">
                                {lineAllocations.map(allocation => {
                                  const transaction = transactions.find(t => t.id === allocation.transaction_id);
                                  if (!transaction) return null;

                                  return (
                                    <div
                                      key={allocation.id}
                                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                      <div className="flex-1">
                                        <p className="font-medium text-gray-900 text-sm">{transaction.label}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          {new Date(transaction.date).toLocaleDateString('fr-FR')}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-xs text-gray-600">Imputé</p>
                                          <p className="font-semibold text-gray-900 text-sm">
                                            {Number(allocation.allocated_amount).toFixed(2)} €
                                          </p>
                                        </div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteAllocation(allocation.id);
                                          }}
                                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Supprimer l'imputation"
                                        >
                                          <Unlink size={16} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Allocations manuelles */}
                            {manualAllocations.filter(a => a.budget_line_id === line.id).length > 0 && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <h5 className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
                                  <AlertCircle size={16} className="text-amber-600" />
                                  Allocations manuelles ({manualAllocations.filter(a => a.budget_line_id === line.id).length})
                                </h5>
                                <div className="space-y-2">
                                  {manualAllocations.filter(a => a.budget_line_id === line.id).map(allocation => (
                                    <div
                                      key={allocation.id}
                                      className="flex items-start justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg"
                                    >
                                      <div className="flex-1">
                                        <p className="font-medium text-gray-900 text-sm">{allocation.reason}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          Date : {new Date(allocation.allocation_date).toLocaleDateString('fr-FR')}
                                        </p>
                                        {allocation.notes && (
                                          <p className="text-xs text-gray-600 mt-1 italic">{allocation.notes}</p>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-xs text-gray-600">Imputé</p>
                                          <p className="font-semibold text-amber-700 text-sm">
                                            {Number(allocation.allocated_amount).toFixed(2)} €
                                          </p>
                                        </div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteManualAllocation(allocation.id);
                                          }}
                                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Supprimer l'allocation manuelle"
                                        >
                                          <Unlink size={16} />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Dépenses */}
          {activeTab === 'expense' && (
            <div className="space-y-3">
              {expenseLines.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle size={48} className="mx-auto mb-4 text-gray-400" />
                  <p>Aucune ligne de dépenses pour cet exercice</p>
                </div>
              ) : (
                expenseLines.map(line => {
                  const isExpanded = expandedBudgetLine === line.id;
                  const lineAllocations = allocations.filter(a => a.budget_line_id === line.id);

                  return (
                    <div
                      key={line.id}
                      className={`rounded-lg transition-all border ${
                        isExpanded
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                    >
                      <div
                        onClick={() => toggleBudgetLine(line.id)}
                        className="p-4 cursor-pointer"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">{line.category?.name}</h4>
                            {isExpanded ? (
                              <ChevronUp size={18} className="text-gray-500" />
                            ) : (
                              <ChevronDown size={18} className="text-gray-500" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">
                              {lineAllocations.length} op.
                            </span>
                            <div className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                              line.consumptionRate > 100 ? 'bg-red-100 text-red-700' :
                              line.consumptionRate > 80 ? 'bg-orange-100 text-orange-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {line.consumptionRate.toFixed(0)}%
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-4 gap-3 mb-3">
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Prévu</p>
                            <p className="font-semibold text-gray-900 text-sm">{Number(line.planned_amount).toFixed(2)} €</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Imputé</p>
                            <p className="font-semibold text-blue-600 text-sm">{line.allocated.toFixed(2)} €</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Restant</p>
                            <p className={`font-semibold text-sm ${line.remaining >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                              {line.remaining.toFixed(2)} €
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Taux</p>
                            <p className="font-semibold text-orange-600 text-sm">{line.consumptionRate.toFixed(1)}%</p>
                          </div>
                        </div>

                        <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              line.consumptionRate > 100 ? 'bg-red-500' :
                              line.consumptionRate > 80 ? 'bg-orange-500' :
                              'bg-blue-500'
                            }`}
                            style={{ width: `${Math.min(line.consumptionRate, 100)}%` }}
                          />
                        </div>
                      </div>

                      {/* Détail déplié */}
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-blue-200 bg-white">
                          {/* Alerte allocations manuelles */}
                          {line.hasManualAllocations && (
                            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                              <Info size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                              <div className="text-xs text-amber-800">
                                <p className="font-medium">Cette ligne contient des allocations manuelles exceptionnelles</p>
                                <p className="mt-1">Montant manuel : {line.manualAllocated.toFixed(2)} €</p>
                              </div>
                            </div>
                          )}

                          <div className="pt-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-semibold text-gray-900 text-sm">
                                Opérations imputées ({lineAllocations.length})
                              </h5>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openManualAllocationModal(line.id);
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors"
                                title="Créer une allocation manuelle (cas exceptionnel)"
                              >
                                <Plus size={14} />
                                Allocation manuelle
                              </button>
                            </div>
                            {lineAllocations.length === 0 ? (
                              <div className="text-center py-6 text-gray-500 text-sm">
                                <p>Aucune opération imputée à cette ligne</p>
                              </div>
                            ) : (
                              <div className="space-y-2 max-h-80 overflow-y-auto">
                                {lineAllocations.map(allocation => {
                                  const transaction = transactions.find(t => t.id === allocation.transaction_id);
                                  if (!transaction) return null;

                                  return (
                                    <div
                                      key={allocation.id}
                                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                      <div className="flex-1">
                                        <p className="font-medium text-gray-900 text-sm">{transaction.label}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          {new Date(transaction.date).toLocaleDateString('fr-FR')}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-xs text-gray-600">Imputé</p>
                                          <p className="font-semibold text-gray-900 text-sm">
                                            {Number(allocation.allocated_amount).toFixed(2)} €
                                          </p>
                                        </div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteAllocation(allocation.id);
                                          }}
                                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Supprimer l'imputation"
                                        >
                                          <Unlink size={16} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Allocations manuelles */}
                            {manualAllocations.filter(a => a.budget_line_id === line.id).length > 0 && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <h5 className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
                                  <AlertCircle size={16} className="text-amber-600" />
                                  Allocations manuelles ({manualAllocations.filter(a => a.budget_line_id === line.id).length})
                                </h5>
                                <div className="space-y-2">
                                  {manualAllocations.filter(a => a.budget_line_id === line.id).map(allocation => (
                                    <div
                                      key={allocation.id}
                                      className="flex items-start justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg"
                                    >
                                      <div className="flex-1">
                                        <p className="font-medium text-gray-900 text-sm">{allocation.reason}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          Date : {new Date(allocation.allocation_date).toLocaleDateString('fr-FR')}
                                        </p>
                                        {allocation.notes && (
                                          <p className="text-xs text-gray-600 mt-1 italic">{allocation.notes}</p>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-xs text-gray-600">Imputé</p>
                                          <p className="font-semibold text-amber-700 text-sm">
                                            {Number(allocation.allocated_amount).toFixed(2)} €
                                          </p>
                                        </div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteManualAllocation(allocation.id);
                                          }}
                                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Supprimer l'allocation manuelle"
                                        >
                                          <Unlink size={16} />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Opérations non imputées */}
          {activeTab === 'unallocated' && (
            <div className="space-y-3">
              {unallocatedTransactions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
                  <p className="font-medium text-gray-900 mb-2">Toutes les opérations sont imputées</p>
                  <p className="text-sm">Aucune opération en attente d'imputation</p>
                </div>
              ) : (
                unallocatedTransactions.map(transaction => {
                  const matchingBudget = budgetLines.find(b => b.category_id === transaction.category_id);

                  return (
                    <div
                      key={transaction.id}
                      className="p-4 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-gray-900">{transaction.label}</h4>
                            {transaction.category && (
                              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                {transaction.category.name}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {new Date(transaction.date).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <p className={`font-bold text-lg mb-2 ${Number(transaction.amount) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {Math.abs(Number(transaction.amount)).toFixed(2)} €
                          </p>
                          <button
                            onClick={() => openAllocationModal(transaction.id, matchingBudget?.id)}
                            disabled={budgetLines.length === 0}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Link2 size={14} />
                            Imputer maintenant
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal d'imputation */}
      {showAllocationModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-xl font-semibold mb-4">Imputer une opération</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Opération</label>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">
                    {transactions.find(t => t.id === selectedTransaction)?.label}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Montant: {Math.abs(Number(transactions.find(t => t.id === selectedTransaction)?.amount)).toFixed(2)} €
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ligne budgétaire</label>
                <select
                  value={selectedBudgetLineForModal || ''}
                  onChange={(e) => setSelectedBudgetLineForModal(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Sélectionner une ligne budgétaire</option>
                  {incomeLines.length > 0 && (
                    <optgroup label="Recettes">
                      {incomeLines.map(line => (
                        <option key={line.id} value={line.id}>
                          {line.category?.name} - {Number(line.planned_amount).toFixed(2)} € (Restant: {line.remaining.toFixed(2)} €)
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {expenseLines.length > 0 && (
                    <optgroup label="Dépenses">
                      {expenseLines.map(line => (
                        <option key={line.id} value={line.id}>
                          {line.category?.name} - {Number(line.planned_amount).toFixed(2)} € (Restant: {line.remaining.toFixed(2)} €)
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Montant à imputer (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={allocationAmount}
                  onChange={(e) => setAllocationAmount(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAllocationModal(false);
                  setSelectedTransaction(null);
                  setSelectedBudgetLineForModal(null);
                  setAllocationAmount('');
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={createAllocation}
                disabled={!selectedBudgetLineForModal}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Imputer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal allocation manuelle */}
      {showManualAllocationModal && selectedBudgetLineForModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-2">Allocation manuelle</h3>
            <div className="p-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Utilisation exceptionnelle uniquement</p>
                <p className="mt-1">Cette fonctionnalité doit être utilisée à la marge pour des cas particuliers (ex: subventions reçues avant le début de l'exercice).</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ligne budgétaire</label>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">
                    {budgetLines.find(l => l.id === selectedBudgetLineForModal)?.category?.name}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Prévu: {Number(budgetLines.find(l => l.id === selectedBudgetLineForModal)?.planned_amount).toFixed(2)} €
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant à imputer (€) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={manualAllocationAmount}
                  onChange={(e) => setManualAllocationAmount(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de l'événement <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={manualAllocationDate}
                  onChange={(e) => setManualAllocationDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <p className="text-xs text-gray-500 mt-1">Date de réception de la subvention, du paiement, etc.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Justification <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={manualAllocationReason}
                  onChange={(e) => setManualAllocationReason(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  rows={3}
                  placeholder="Ex: Subvention CAF reçue le 15/08/2024 avant le début de l'exercice 2024-2025"
                  minLength={10}
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 10 caractères - soyez précis</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes complémentaires (optionnel)
                </label>
                <textarea
                  value={manualAllocationNotes}
                  onChange={(e) => setManualAllocationNotes(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  rows={2}
                  placeholder="Informations complémentaires..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowManualAllocationModal(false);
                  setSelectedBudgetLineForModal(null);
                  setManualAllocationAmount('');
                  setManualAllocationDate('');
                  setManualAllocationReason('');
                  setManualAllocationNotes('');
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={createManualAllocation}
                disabled={!manualAllocationAmount || !manualAllocationDate || !manualAllocationReason || manualAllocationReason.length < 10}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Créer l'allocation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exercise Landing Drawer */}
      <ExerciseLandingDrawer
        isOpen={isLandingDrawerOpen}
        onClose={() => setIsLandingDrawerOpen(false)}
        year={currentYear}
      />
    </div>
  );
}
